import React, { useState } from 'react';
import {
  Box,
  Stack,
  Group,
  Button,
  Modal,
  TextInput,
  Select,
  ActionIcon,
  Paper,
  Text,
  Badge,
  Flex,
  Menu,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconGripVertical, IconDots, IconDownload,
} from '@tabler/icons-react';
import { PropertyDefinition, PropertyType, createProperty } from '../../../types/models/Property';

interface PropertyManagerProps {
  properties: PropertyDefinition[];
  onPropertiesChange: (properties: PropertyDefinition[]) => void;
  /**
   * F 377 — the Export control moved here from the database toolbar.
   *
   * Optional so this component still renders standalone (its own tests do).
   * When absent the button is not rendered at all — never a dead control.
   */
  onExport?: () => void;
}

const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'status', label: 'Status' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'image', label: 'Image' },
];

/**
 * Property Manager Component
 * Allows adding, editing, deleting, and reordering properties
 */
export const PropertyManager: React.FC<PropertyManagerProps> = ({
  properties,
  onPropertiesChange,
  onExport,
}) => {
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState<PropertyDefinition | null>(null);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyType, setNewPropertyType] = useState<PropertyType>('text');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Handle add property
  const handleAddProperty = () => {
    if (!newPropertyName.trim()) return;

    const newProperty = createProperty(
      `prop_${Date.now()}`,
      newPropertyName,
      newPropertyType,
      properties.length
    );

    onPropertiesChange([...properties, newProperty]);
    setIsAddingProperty(false);
    setNewPropertyName('');
    setNewPropertyType('text');
  };

  // Handle edit property
  const handleEditProperty = (property: PropertyDefinition) => {
    setEditingProperty(property);
  };

  // Handle save edited property
  const handleSaveEditedProperty = (property: PropertyDefinition) => {
    onPropertiesChange(
      properties.map(p => (p.id === property.id ? property : p))
    );
    setEditingProperty(null);
  };

  // Handle delete property
  const handleDeleteProperty = (propertyId: string) => {
    if (confirm('Delete this property?')) {
      onPropertiesChange(properties.filter(p => p.id !== propertyId));
    }
  };

  // Handle property reorder
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newProperties = [...properties];
    const [draggedProperty] = newProperties.splice(draggedIndex, 1);
    newProperties.splice(targetIndex, 0, draggedProperty);

    // Update positions
    newProperties.forEach((prop, idx) => {
      prop.position = idx;
    });

    onPropertiesChange(newProperties);
    setDraggedIndex(null);
  };

  // Handle toggle visibility
  const handleToggleVisibility = (propertyId: string) => {
    onPropertiesChange(
      properties.map(p =>
        p.id === propertyId ? { ...p, visible: !p.visible } : p
      )
    );
  };

  return (
    <Stack gap="md">
      {/* F 137 DEFECT B — the modal chrome already renders the title
          ("Database Properties"), so this one made the dialog show its name
          twice. The body now carries the action only. The button moves into
          the same row so the header does not read as a bare bar. */}
      <Group justify="space-between" align="center">
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={() => setIsAddingProperty(true)}
        >
          Add Property
        </Button>

        {/* F 377 — Export now lives HERE rather than in the database toolbar.
            Notion keeps export inside settings, out of the primary action row.
            The capability is preserved: the same `handleExport` is threaded down
            from DatabaseView, so nothing was removed from the product, only
            relocated. Rendered only when a handler was actually passed — an
            always-visible button that silently does nothing is worse than none. */}
        {onExport && (
          <Button
            data-fc-export-btn=""
            variant="subtle"
            size="sm"
            leftSection={<IconDownload size={16} />}
            onClick={onExport}
          >
            Export
          </Button>
        )}
      </Group>

      {/* F 377 — DENSITY. These were a bordered <Paper p="sm"> per property,
          inside a <Stack gap="sm">: a card around every row PLUS a gap between
          rows, which measured ~82px tall for ~24px of content. Notion uses flat
          rows separated by hairline dividers with no gaps.

          One bordered container, `gap={0}`, rows at `p="xs"`. The drag handlers,
          opacity feedback and grab cursor are UNTOUCHED — reordering is
          behaviour, and density work must not quietly disable it. */}
      <Stack gap={0} style={{
        border: '1px solid var(--fc-border-color, rgba(0,0,0,0.12))',
        borderRadius: 6, overflow: 'hidden',
      }}>
        {properties.length === 0 ? (
          <Text c="dimmed" size="sm">
            {/* F 137 DEFECT C — "Add one to get started" read as a dead end
                while the Add Property button sat directly above it. Empty
                states should point at the action, not signal a stop. */}
            Click Add Property to create your first property.
          </Text>
        ) : (
          properties.map((property, index) => (
            <Box
              key={property.id}
              data-fc-prop-row={property.id}
              p="xs"
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              style={{
                opacity: draggedIndex === index ? 0.5 : 1,
                cursor: 'grab',
                borderTop: index > 0
                  ? '1px solid var(--fc-border-color, rgba(0,0,0,0.08))'
                  : undefined,
              }}
            >
              <Flex gap="sm" align="center">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  style={{ cursor: 'grab' }}
                >
                  <IconGripVertical size={14} />
                </ActionIcon>

                <Stack gap={0} style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {property.name}
                  </Text>
                  <Badge size="sm" variant="light">
                    {property.type}
                  </Badge>
                </Stack>

                <Group gap={4}>
                  <Menu shadow="md">
                    <Menu.Target>
                      <ActionIcon size="sm" variant="subtle">
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEdit size={14} />}
                        onClick={() => handleEditProperty(property)}
                      >
                        Edit
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => handleToggleVisibility(property.id)}
                      >
                        {property.visible ? 'Hide' : 'Show'}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => handleDeleteProperty(property.id)}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Flex>
            </Box>
          ))
        )}
      </Stack>

      {/* Add Property Modal */}
      {/* ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE NESTED MODAL NEEDS A Z-INDEX ABOVE ITS
        PARENT. ⇒⇒⇒⇒⇒⇒⇒⇒ The "Database Properties" modal is rendered with
        `zIndex={10000}` (FreshCards' own <Modal> does that; Mantine's does
        ⇒⇒⇒⇒⇒⇒⇒⇒ not). ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒⇒⇒⇒⇒⇒⇒⇒⇒
        ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ WITHOUT THIS, THE INNER MODAL RENDERS *UNDER*
        ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PROPERTY LIST**, AND THE LIVE CLICKTEST MEASURED
        ⇒⇒□ ⇒⇒□ ⇒⇒□ IT: the "Create" button was unclickable because a
        ⇒⇒□ ⇒⇒□ ⇒⇒□ `data-fc-prop-row="date"` row intercepted the pointer
        ⇒⇒□ ⇒⇒□ ⇒⇒□ events, and the parent's overlay intercepted them on
        ⇒⇒□ ⇒⇒□ ⇒⇒□ the next retry. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE INNER
        ⇒⇒□ ⇒⇒□ ⇒⇒□ MODAL MUST SIT ABOVE THE PARENT, NOT BESIDE IT. */}
      <Modal
        opened={isAddingProperty}
        onClose={() => {
          setIsAddingProperty(false);
          setNewPropertyName('');
          setNewPropertyType('text');
        }}
        title="Add Property"
        size="sm"
        zIndex={10001}
      >
        <Stack>
          <TextInput
            label="Property Name"
            placeholder="e.g., Status, Priority"
            value={newPropertyName}
            onChange={(e) => setNewPropertyName(e.currentTarget.value)}
          />
          <Select
            label="Property Type"
            placeholder="Select type"
            data={PROPERTY_TYPES}
            value={newPropertyType}
            onChange={(val) => setNewPropertyType(val as PropertyType)}
            maxDropdownHeight={400}
            searchable
            // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE DROPDOWN MUST RENDER INSIDE THE MODAL,
            // ⇒⇒⇒⇒⇒⇒⇒⇒ NOT IN A PORTAL. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED AGAINST PRODUCTION: THE ADD
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PROPERTY MODAL IS NESTED INSIDE THE
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ "DATABASE PROPERTIES" MODAL, AND A PORTALLED
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ DROPDOWN LANDS *BEHIND* THE PARENT'S
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `mantine-Modal-overlay`** — so the live
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ clicktest could not click the "Date"
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OPTION: "mantine-Modal-overlay …
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ intercepts pointer events". ⇒⇒□ ⇒⇒□ ⇒⇒□
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `withinPortal={false}` RENDERS THE
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ DROPDOWN INSIDE THIS MODAL, WHICH IS
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ABOVE THE PARENT'S OVERLAY.**
            withinPortal={false}
            // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — AND THE DROPDOWN NEEDS A Z-INDEX ABOVE
            // ⇒⇒⇒⇒⇒⇒⇒⇒ THE *PARENT* MODAL'S OVERLAY. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□
            // ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED AGAINST PRODUCTION: WITH THE
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ DROPDOWN OPEN, THE STACK WAS `mantine-Popover-
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ dropdown` AT `z-index: 300` INSIDE A MODAL
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ WHOSE SIBLING — THE PARENT'S `mantine-Modal-
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ overlay` — SITS AT `z-index: 10000`.** ⇒⇒□
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE DROPDOWN WAS UNDER THE PARENT'S
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OVERLAY AND COULD NOT BE CLICKED.**
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `withinPortal={false}` PUTS IT
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ INSIDE THE INNER MODAL; `zIndex` PUTS IT
            // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ABOVE THE OUTER ONE.**
            styles={{
              dropdown: { zIndex: 10002 },
            }}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setIsAddingProperty(false);
                setNewPropertyName('');
                setNewPropertyType('text');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddProperty} disabled={!newPropertyName.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Property Modal */}
      {editingProperty && (
        <Modal
          opened={!!editingProperty}
          onClose={() => setEditingProperty(null)}
          title="Edit Property"
          size="sm"
        >
          <Stack>
            <TextInput
              label="Property Name"
              value={editingProperty.name}
              onChange={(e) =>
                setEditingProperty({
                  ...editingProperty,
                  name: e.currentTarget.value,
                })
              }
            />
            <Text size="sm" c="dimmed">
              Type: {editingProperty.type}
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setEditingProperty(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleSaveEditedProperty(editingProperty)}>
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
};

export default PropertyManager;
